/**
 * CredentialStatusCard — prominent edit-page status card for dashboard-eligible
 * credentials.
 *
 * Renders directly above the credential form card on `/credentials/edit/:id`.
 * Shows the §38.5 anatomy: 4px state-colored left rail, 40px icon tile, 16px
 * headline, reason-aware subcopy, "Last tested {relative}" meta, and a
 * right-aligned primary action button.
 *
 * For workflow-only credentials (no `connectionStatus`) renders NOTHING — the
 * form starts at the first field. Absence is the signal (§38.6).
 *
 * The card starts in resting state from `connectionStatus`; clicking the action
 * (when it's a test) transitions locally to `testing` then resolves in place.
 * Recovery actions (Reconnect / Fix access) delegate to parent callbacks.
 *
 * Accessibility (§38.8):
 *   - testing state body wrapped in role="status" aria-live="polite".
 *   - Action button keeps focus during testing (disabled + aria-busy).
 *   - "Last tested" exposes absolute via title.
 *
 * Dashboard-era only. Brand guide §38.5. Slice 3.1 — credential-status Epic 3.
 */

import { useState, useCallback, useEffect, useRef, CSSProperties } from 'react'
import { useSelector } from 'react-redux'
import {
  CheckCircle2,
  XCircle,
  ShieldAlert,
  CircleDashed,
  Loader2,
  HelpCircle,
  Wifi,
  RefreshCw,
  ExternalLink,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { RootState } from '@/redux'
import type { CredentialConnectionStatus } from '@/types/credentials'
import { CredentialCapabilityAPI } from '@/API/CredentialCapability'
import {
  DisplayStatusState,
  STATUS_PRESENTATION,
  STATUS_SUBCOPY,
  deriveStatusFromPerDomainMap,
  formatRelativeTime,
  formatAbsoluteTime,
} from './credentialStatus'

// ─── Icon maps ────────────────────────────────────────────────────────────────

type IconProps = { size: number; className?: string; 'aria-hidden': true }
type IconFC = (props: IconProps) => JSX.Element

const CARD_ICON: Record<DisplayStatusState, IconFC> = {
  valid: (props) => <CheckCircle2 {...props} />,
  invalid_key: (props) => <XCircle {...props} />,
  missing_permissions: (props) => <ShieldAlert {...props} />,
  not_tested_yet: (props) => <CircleDashed {...props} />,
  testing: (props) => <Loader2 {...props} className={`${props.className ?? ''} animate-spin`.trim()} />,
  couldnt_verify: (props) => <HelpCircle {...props} />,
}

// Action icon per state.
function ActionIcon({ state }: { state: DisplayStatusState }) {
  if (state === 'invalid_key') return <RefreshCw size={15} aria-hidden="true" />
  if (state === 'missing_permissions') return <ExternalLink size={15} aria-hidden="true" />
  if (state === 'testing') return <Loader2 size={15} className="animate-spin" aria-hidden="true" />
  if (state === 'couldnt_verify') return <RefreshCw size={15} aria-hidden="true" />
  return <Wifi size={15} aria-hidden="true" />
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface CredentialStatusCardProps {
  credentialId: string
  connectionStatus: CredentialConnectionStatus | undefined
  /** Called when user clicks "Reconnect" (invalid_key — §37e HarvestModal). */
  onReconnect: () => void
  /** Called when user clicks "Fix access" (missing_permissions — §37g panel). */
  onFixAccess: () => void
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CredentialStatusCard = ({
  credentialId,
  connectionStatus,
  onReconnect,
  onFixAccess,
}: CredentialStatusCardProps) => {
  const token = useSelector((s: RootState) => s.user.token)

  // All hooks must be called before any early return (React Rules of Hooks).
  const [isProbing, setIsProbing] = useState(false)
  const [localState, setLocalState] = useState<DisplayStatusState | null>(null)
  const [localLastTestedAt, setLocalLastTestedAt] = useState<string | null>(null)

  // Guards post-await setState if the card unmounts mid-probe (navigate away).
  const isMountedRef = useRef(true)
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const handleTest = useCallback(async () => {
    if (!token || isProbing) return
    setIsProbing(true)
    setLocalState(null)

    const resp = await CredentialCapabilityAPI.probeAllDomains(token, credentialId)

    if (!isMountedRef.current) return

    if (resp.err !== undefined || resp.data === undefined) {
      setLocalState('couldnt_verify')
    } else {
      // resp.data is CredentialProbeRawMap (Record<domainId, CredentialCapabilityAvailability>).
      setLocalState(deriveStatusFromPerDomainMap(resp.data))
    }

    setLocalLastTestedAt(new Date().toISOString())
    setIsProbing(false)
  }, [token, credentialId, isProbing])

  // §38.6: workflow-only credentials render nothing — after hooks.
  if (connectionStatus === undefined) {
    return null
  }

  const displayState: DisplayStatusState = isProbing
    ? 'testing'
    : localState ?? connectionStatus.state
  const displayLastTestedAt: string | null =
    localLastTestedAt ?? connectionStatus.lastTestedAt

  const pres = STATUS_PRESENTATION[displayState]
  const IconComponent = CARD_ICON[displayState]

  // ─── Action button handler ──────────────────────────────────────────────────

  const handleAction = () => {
    if (isProbing) return
    if (displayState === 'invalid_key') {
      onReconnect()
      return
    }
    if (displayState === 'missing_permissions') {
      onFixAccess()
      return
    }
    void handleTest()
  }

  // ─── Styles (inline for dynamic AA-safe colors — §38.3) ─────────────────────

  // NOTE: property ordering is load-bearing — borderLeftColor MUST be declared
  // after borderColor (the shorthand). React serializes inline-style properties
  // in declaration order, so the shorthand wins the cascade if it comes last
  // and clobbers the vivid left-rail color with the light tint. Do not reorder.
  const cardStyle: CSSProperties = {
    borderColor: pres.borderColor,   // light tint: top / right / bottom
    borderLeftColor: pres.color,     // vivid state color: 4px left rail — longhand after shorthand
    backgroundColor: pres.bgColor,
    borderLeftWidth: '4px',
  }

  const iconTileStyle: CSSProperties = {
    color: pres.color,
    backgroundColor:
      displayState === 'valid'
        ? pres.bgColor
        : '#FFFFFF',
  }

  const headlineStyle: CSSProperties = {
    color:
      displayState === 'not_tested_yet' || displayState === 'couldnt_verify'
        ? '#0F172A' // text-primary for neutral states per mockup
        : pres.color,
  }

  const relativeTime = formatRelativeTime(displayLastTestedAt)
  const absoluteTime = formatAbsoluteTime(displayLastTestedAt)
  const showLastTested =
    relativeTime !== null &&
    displayState !== 'not_tested_yet' &&
    displayState !== 'testing'

  // ─── Action button styles per variant ────────────────────────────────────────

  const getActionButtonStyle = (): CSSProperties | undefined => {
    const variant = pres.actionVariant
    if (variant === 'solid-red') {
      return { backgroundColor: 'hsl(0, 63%, 42%)', color: '#fff', borderColor: 'hsl(0, 63%, 42%)' }
    }
    if (variant === 'solid-amber') {
      return { backgroundColor: 'hsl(34, 100%, 30%)', color: '#fff', borderColor: 'hsl(34, 100%, 30%)' }
    }
    if (variant === 'solid-accent') {
      return { backgroundColor: '#2563EB', color: '#fff', borderColor: '#2563EB' }
    }
    return undefined
  }

  const actionButtonStyle = getActionButtonStyle()

  return (
    <div
      className="flex items-start gap-4 border rounded-[10px] p-[18px_20px] mb-4"
      style={cardStyle}
      data-testid={`status-card-${credentialId}`}
    >
      {/* Left: 40px icon tile */}
      <div
        className="w-10 h-10 rounded-[9px] flex items-center justify-center flex-shrink-0"
        style={iconTileStyle}
      >
        <IconComponent size={22} aria-hidden={true} />
      </div>

      {/* Center: headline + subcopy + last tested */}
      <div
        className="flex-1 min-w-0"
        role={displayState === 'testing' ? 'status' : undefined}
        aria-live={displayState === 'testing' ? 'polite' : undefined}
      >
        <div
          className="text-base font-bold leading-tight"
          style={headlineStyle}
        >
          {pres.headline}
        </div>
        <div className="text-[13.5px] text-secondary mt-1 leading-relaxed">
          {STATUS_SUBCOPY[displayState]}
        </div>
        {showLastTested && (
          <div className="text-[12.5px] text-muted-foreground mt-2 inline-flex items-center gap-1">
            Last tested{' '}
            <span
              className="border-b border-dotted border-border pb-px cursor-default"
              title={absoluteTime ?? undefined}
            >
              {relativeTime}
            </span>
          </div>
        )}
      </div>

      {/* Right: action button */}
      <div className="flex-shrink-0 self-center">
        <Button
          type="button"
          variant={pres.actionVariant === 'outline' ? 'outline' : 'default'}
          size="default"
          onClick={handleAction}
          aria-disabled={isProbing || undefined}
          aria-busy={isProbing || undefined}
          style={actionButtonStyle}
          className={`focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2${
            isProbing ? ' opacity-60 cursor-default' : ''
          }`}
          data-testid={`status-card-action-${credentialId}`}
        >
          <ActionIcon state={displayState} />
          {pres.actionLabel}
        </Button>
      </div>
    </div>
  )
}
